import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from './useAuth';

interface DashboardStats {
  planCompletion: number;
  lastUpdated: string | null;
  riskLevel: 'low' | 'medium' | 'high' | null;
  actionItemsCount: number;
  interviewCount: number;
  planCount: number;
}

interface RecentActivity {
  id: string;
  action: string;
  time: string;
}

interface DashboardData {
  stats: DashboardStats;
  recentActivity: RecentActivity[];
  userName: string | null;
  loading: boolean;
  error: string | null;
}

export const useDashboardData = (): DashboardData => {
  const { user } = useAuth();
  const [stats, setStats] = useState<DashboardStats>({
    planCompletion: 0,
    lastUpdated: null,
    riskLevel: null,
    actionItemsCount: 0,
    interviewCount: 0,
    planCount: 0,
  });
  const [recentActivity, setRecentActivity] = useState<RecentActivity[]>([]);
  const [userName, setUserName] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchDashboardData = async () => {
      if (!user) {
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        setError(null);

        // Fetch user profile
        const { data: profile } = await supabase
          .from('profiles')
          .select('full_name')
          .eq('id', user.id)
          .single();

        setUserName(profile?.full_name || null);

        // Fetch interviews
        const { data: interviews } = await supabase
          .from('interviews')
          .select('*')
          .eq('user_id', user.id)
          .order('created_at', { ascending: false });

        // Fetch emergency plans
        const { data: plans } = await supabase
          .from('emergency_plans')
          .select('*')
          .eq('user_id', user.id)
          .order('created_at', { ascending: false });

        const interviewCount = interviews?.length || 0;
        const planCount = plans?.length || 0;
        const completedInterviews = interviews?.filter(i => i.status === 'completed').length || 0;
        
        // Calculate plan completion
        const planCompletion = interviewCount > 0 
          ? Math.round((completedInterviews / interviewCount) * 100)
          : 0;

        // Get last updated
        const lastUpdated = plans?.[0]?.updated_at || interviews?.[0]?.updated_at || null;

        // Calculate risk level from latest interview location
        let riskLevel: 'low' | 'medium' | 'high' | null = null;
        if (interviews && interviews.length > 0 && interviews[0].location) {
          const emergencyTypes = interviews[0].emergency_types || [];
          if (emergencyTypes.length >= 3) riskLevel = 'high';
          else if (emergencyTypes.length >= 2) riskLevel = 'medium';
          else if (emergencyTypes.length >= 1) riskLevel = 'low';
        }

        // Count action items (incomplete interviews + plans without PDFs)
        const incompleteInterviews = interviews?.filter(i => i.status === 'in_progress').length || 0;
        const plansWithoutPdf = plans?.filter(p => !p.pdf_url).length || 0;
        const actionItemsCount = incompleteInterviews + plansWithoutPdf;

        setStats({
          planCompletion,
          lastUpdated,
          riskLevel,
          actionItemsCount,
          interviewCount,
          planCount,
        });

        // Build recent activity
        const activities: RecentActivity[] = [];
        
        if (plans && plans.length > 0) {
          activities.push({
            id: plans[0].id,
            action: 'Emergency plan generated',
            time: new Date(plans[0].created_at).toLocaleString(),
          });
        }

        if (interviews && interviews.length > 0) {
          const latestInterview = interviews[0];
          if (latestInterview.status === 'completed') {
            activities.push({
              id: latestInterview.id,
              action: 'Completed interview',
              time: new Date(latestInterview.completed_at || latestInterview.updated_at).toLocaleString(),
            });
          } else {
            activities.push({
              id: latestInterview.id,
              action: 'Started interview',
              time: new Date(latestInterview.created_at).toLocaleString(),
            });
          }
        }

        setRecentActivity(activities.slice(0, 5));
      } catch (err) {
        console.error('Error fetching dashboard data:', err);
        setError('Failed to load dashboard data');
      } finally {
        setLoading(false);
      }
    };

    fetchDashboardData();
  }, [user]);

  return {
    stats,
    recentActivity,
    userName,
    loading,
    error,
  };
};
